import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  IconButton,
  TextField,
  Grid,
  Box,
  Divider
} from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import { conditionTypes } from './constants';
import useBuilder from './useBuilder';
import Skeleton from 'react-loading-skeleton';
import { SelectInput } from 'components';
import { getEnvVar } from "helpers";
import { excludeSurveyTools } from 'constants.js';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  button: {
    margin: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  containerMargin: {
    marginLeft: theme.spacing(3)
  },
  selectContainer: {
    display: 'inline',
    minWidth: '12rem',
    margin: theme.spacing(1)
  },
  buttonContainer: {
    display: 'inline',
    margin: theme.spacing(1)
  },
  ruleError: {
    color: theme.palette.error.main,
    paddingTop: '13px'
  },
  dateField: {
    width: '100%',
    marginTop: '0px'
  },
  templateButtonError: {
    paddingLeft: '19px',
    color: theme.palette.error.main
  },
  ruleDivider: {
    marginTop: '10px',
    marginBottom: '10px'
  }
}));

const ActionBuilder = props => {
  const {
    className,
    staticContext,
    survey,
    campaigns,
    back,
    showNav,
    ...rest
  } = props;
  const {
    thisSurvey,
    handleChange,
    onFocus,
    onBlur,
    shouldShowError,
    getErrorMessage,
    isLoading,
    isGettingCampaign,
    isTemplateReadOnly
  } = survey;

  const {
    actionModels,
    addActionModel,
    updateActionModel,
    deleteActionModel
  } = useBuilder({
    thisSurvey,
    handleChange
  });
  const classes = useStyles();

  const getValidCampaigns = action => {
    return campaigns.filter(
      campaign => campaign.active || action?.campaignUuid === campaign.uuid
    );
  };

  const admExcludeSurveyTools = getEnvVar('ADM_EXCLUDE_SURVEY_TOOLS')?.split(',') || [...excludeSurveyTools];
  const surveyQuestions = {
    list: thisSurvey?.questions?.filter(question => !admExcludeSurveyTools.includes(question.type)),
    keyId: 'name',
    valueId: 'name',
    displayValueId: ['title', 'name']
  };

  const operators = {
    list: conditionTypes,
    keyId: 'type',
    valueId: 'type',
    displayValueId: 'displayName'
  };

  const getSubQuestionOptions = options => {
    return {
      list: options || [],
      keyId: 'value',
      valueId: ['value', 'option'],
      displayValueId: ['text', 'value', 'option']
    };
  };
  
  const getChoices = choices => {
    return {
      list: choices,
      keyId: 'value',
      valueId: ['value', 'option'],
      displayValueId: ['text', 'value', 'option']
    };
  };

  const getCampaigns = action => {
    return {
      list: getValidCampaigns(action),
      keyId: 'uuid',
      valueId: 'uuid',
      displayValueId: 'name'
    };
  };

  // Question types that support multiple choices/options
  // These should show dropdown even when all options are removed
  const QUESTION_TYPES_WITH_CHOICES = [
    'radiogroup',
    'dropdown',
    'checkbox',
    'matrix',
    'matrixdropdown',
    'matrixdynamic'
  ];

  const questionSupportsChoices = (questionType) => {
    return QUESTION_TYPES_WITH_CHOICES.includes(questionType);
  };

  // Determine if should show dropdown: check type first, fallback to choices array length
  // Fallback is needed for newly added actions that may not have type set yet
  const shouldShowChoicesDropdown = (action) => {
    if (action?.type) {
      return questionSupportsChoices(action.type);
    }
    // Fallback: if type not yet set, check if choices array has items
    return action?.choices?.length > 0;
  };

  return (
    <div {...rest} className={clsx(classes.root, className)}>
      {!isTemplateReadOnly() && (
        <Button
          variant="contained"
          color="primary"
          // disabled={}
          className={classes.button}
          startIcon={<Add />}
          onClick={addActionModel}>
          Add Action
        </Button>
      )}

      {actionModels?.map((action, i) =>
        isGettingCampaign && thisSurvey?.questions ? (
          <>
            <Divider className={classes.ruleDivider} />
            <Skeleton
              style={{ marginLeft: '8px' }}
              variant="rectangular"
              width={300}
              height={20}
            />
          </>
        ) : (
          <Box key={i}>
            <Divider className={classes.ruleDivider} />
            <Grid container>
              <Grid item className={classes.selectContainer}>
                <SelectInput
                  fullWidth
                  label="Select Question"
                  margin="dense"
                  name="surveyQuestionName"
                  onChange={e => updateActionModel(i, e)}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  disabled={isTemplateReadOnly()}
                  required
                  value={action.surveyQuestionName || ''}
                  error={shouldShowError(
                    `surveyQuestionResponseRules.${i}.surveyQuestionName`
                  )}
                  helperText={getErrorMessage(
                    `surveyQuestionResponseRules.${i}.surveyQuestionName`
                  )}
                  options={surveyQuestions}
                />
              </Grid>
              {action?.surveyQuestionName !== '' && action?.isMatrix && (
                <Grid item className={classes.selectContainer}>
                  <SelectInput
                    fullWidth
                    label="Select Sub Question"
                    margin="dense"
                    name="matrixSubQuestionName"
                    onChange={e => updateActionModel(i, e)}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={isTemplateReadOnly()}
                    required
                    value={action.matrixSubQuestionName || ''}
                    error={shouldShowError(
                      `surveyQuestionResponseRules.${i}.matrixSubQuestionName`
                    )}
                    helperText={getErrorMessage(
                      `surveyQuestionResponseRules.${i}.matrixSubQuestionName`
                    )}
                    options={getSubQuestionOptions(action?.subQuestionOptions)}
                  />
                </Grid>
              )}
              {(action?.isMatrix ? action?.matrixSubQuestionName !== '' : action?.surveyQuestionName !== '') && (
                <Grid item className={classes.selectContainer}>
                  <SelectInput
                    fullWidth
                    label="Select Operator"
                    margin="dense"
                    name="condition"
                    onChange={e => updateActionModel(i, e)}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={isTemplateReadOnly()}
                    required
                    value={action?.conditionData?.condition || ''}
                    error={shouldShowError(
                      `surveyQuestionResponseRules.${i}.conditionData.condition`
                    )}
                    helperText={getErrorMessage(
                      `surveyQuestionResponseRules.${i}.conditionData.condition`
                    )}
                    options={operators}
                  />
                </Grid>
              )}
              {action?.conditionData?.condition !== '' ? (
                shouldShowChoicesDropdown(action) ? (
                  <Grid item className={classes.selectContainer}>
                    <SelectInput
                      fullWidth
                      label="Select Response"
                      margin="dense"
                      name="conditionValue"
                      onChange={e => updateActionModel(i, e)}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      disabled={isTemplateReadOnly()}
                      required
                      value={action?.conditionData?.conditionValue || ''}
                      error={shouldShowError(
                        `surveyQuestionResponseRules.${i}.conditionData.conditionValue`
                      )}
                      helperText={getErrorMessage(
                        `surveyQuestionResponseRules.${i}.conditionData.conditionValue`
                      )}
                      options={getChoices(action?.choices)}
                    />
                  </Grid>
                ) : (
                    <Grid item className={classes.selectContainer}>
                      <TextField
                        fullWidth
                        label="Response"
                        margin="dense"
                        name="conditionValue"
                        onChange={e => updateActionModel(i, e)}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        disabled={isTemplateReadOnly()}
                        required
                        value={action?.conditionData?.conditionValue || ''}
                        error={shouldShowError(
                          `surveyQuestionResponseRules.${i}.conditionData.conditionValue`
                        )}
                        helperText={getErrorMessage(
                          `surveyQuestionResponseRules.${i}.conditionData.conditionValue`
                        )}
                        variant="outlined"></TextField>
                    </Grid>
                )
              ) : null}
              {action?.conditionData?.conditionValue !== '' && (
                <Grid item className={classes.selectContainer}>
                  <SelectInput
                    fullWidth
                    label="Select campaign"
                    margin="dense"
                    name="campaignUuid"
                    onChange={e => updateActionModel(i, e)}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={isTemplateReadOnly()}
                    required
                    value={action?.campaignUuid || ''}
                    error={shouldShowError(
                      `surveyQuestionResponseRules.${i}.campaignUuid`
                    )}
                    helperText={getErrorMessage(
                      `surveyQuestionResponseRules.${i}.campaignUuid`
                    )}
                    options={getCampaigns(action)}
                    groupBy={'campaignOccurrenceType'}
                  />
                </Grid>
              )}
              <Grid item className={classes.buttonContainer}>
                {!isTemplateReadOnly() && (
                  <IconButton
                    className={classes.deleteButton}
                    onClick={e => deleteActionModel(i)}>
                    <Delete />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        )
      )}
    </div>
  );
};

export default ActionBuilder;
